import type { NAuth } from '@app/types/Auth';
import type { NSchedule } from '@app/types/Schedule';

import type { NWorkSchedule } from '@pages/WorkSchedule/WorkSchedule.types';

import { adaptTimeRange } from './adaptTimeRange';
import { generateAddingScheduleId } from './generateAddingScheduleId';
import { isActionsAllowed } from './isActionsAllowed';

import type { BaseOptionType } from 'antd/es/select';
import type { Dispatch, SetStateAction } from 'react';

export const changeFewSchedules = (
  period: string[],
  employeeId: number,
  startTime: BaseOptionType,
  endTime: BaseOptionType,
  activeBranchId: NAuth.UserPrint['activeBranchId'],
  mode: NWorkSchedule.ScheduleMode,
  cells: NWorkSchedule.CellsMap,
  changingCells: NWorkSchedule.CellsMap,
  deletingCells: NWorkSchedule.CellsMap,
  setChangingCells: Dispatch<SetStateAction<NWorkSchedule.CellsMap>>,
  setDeletingCells: Dispatch<SetStateAction<NWorkSchedule.CellsMap>>,
  rights: {
    creationInPresentAndFeature: boolean;
    editionInPresentAndFeature: boolean;
    deletionInPresentAndFeature: boolean;
    editionInPast: boolean;
    deletionInPast: boolean;
  }
) => {
  const _changingSchedules = new Map([...changingCells]);
  const _schedulesToDelete = new Map([...deletingCells]);

  (period || []).forEach((date: string) => {
    const cell = cells?.get(employeeId)?.get(date);

    const isPreloaded = !!cell?.schedule;

    if (
      !isActionsAllowed({
        date,
        schedule: isPreloaded,
        mode,
        rights,
      })
    ) {
      return;
    }

    const isChanging =
      _changingSchedules?.has(employeeId) &&
      _changingSchedules.get(employeeId)?.has(date) &&
      _changingSchedules.get(employeeId)?.get(date);

    const isDeleting =
      _schedulesToDelete?.has(employeeId) &&
      _schedulesToDelete.get(employeeId)?.has(date) &&
      _schedulesToDelete.get(employeeId)?.get(date);

    if (isDeleting) {
      _schedulesToDelete.get(employeeId)?.delete(date);
    } else if (isChanging) {
      _changingSchedules.get(employeeId)?.delete(date);
    } else if (isPreloaded) {
      if (mode === 'delete') {
        _schedulesToDelete.has(employeeId)
          ? _schedulesToDelete.get(employeeId)?.set(date, { ...cell })
          : _schedulesToDelete
              .set(employeeId, new Map())
              .get(employeeId)
              ?.set(date, { ...cell });
      } else {
        const newCell = { ...cell };
        newCell.schedule.TimeS = `${date}T${startTime.current.label}:00`;
        newCell.schedule.TimeE = `${date}T${endTime.current.label}:00`;
        newCell.time = adaptTimeRange(
          newCell.schedule.TimeS,
          newCell.schedule.TimeE
        );

        _changingSchedules.has(employeeId)
          ? _changingSchedules.get(employeeId)?.set(date, newCell)
          : _changingSchedules
              .set(employeeId, new Map())
              .get(employeeId)
              ?.set(date, newCell);
      }
    } else {
      const newId = generateAddingScheduleId();
      const schedule: NSchedule.ModelWorkSchedule | null = {
        Id: newId,
        UserId: Number(employeeId),
        BranchId: Number(activeBranchId),
        TimeS: `${date}T${startTime.current.label}:00`,
        TimeE: `${date}T${endTime.current.label}:00`,
        IsIgnoreRecords: false,
      };
      const newCell = {
        schedule,
        time: adaptTimeRange(schedule.TimeS, schedule.TimeE),
        records: 0,
      };

      _changingSchedules.has(employeeId)
        ? _changingSchedules.get(employeeId)?.set(date, newCell)
        : _changingSchedules
            .set(employeeId, new Map())
            .get(employeeId)
            ?.set(date, newCell);
    }
  });

  setChangingCells(_changingSchedules);
  setDeletingCells(_schedulesToDelete);
};
